import { useEffect, useState } from 'react'
import type { SupabaseClient } from '@supabase/supabase-js'
import { establishAuthSessionFromUrl, isDeadAuthLinkUrl } from '@/lib/auth-callback'

type RecoverySessionState = {
  ready: boolean
  checking: boolean
  error: string | null
}

/**
 * Wait for Supabase recovery/invite session from email link before updateUser({ password }).
 */
export function useRecoverySession(client: SupabaseClient | null): RecoverySessionState {
  const [ready, setReady] = useState(false)
  const [checking, setChecking] = useState(Boolean(client))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!client) {
      setChecking(false)
      return
    }

    let cancelled = false

    const finish = (sessionOk: boolean, err: string | null) => {
      if (cancelled) return
      setReady(sessionOk)
      setError(err)
      setChecking(false)
    }

    const deadLink = isDeadAuthLinkUrl()

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((event, session) => {
      if (deadLink) return
      if (event === 'PASSWORD_RECOVERY' || (session && isRecoveryLikeEvent(event))) {
        finish(true, null)
      }
    })

    void (async () => {
      const result = await establishAuthSessionFromUrl(client)
      if (cancelled) return
      if (deadLink || isDeadAuthLinkUrl()) {
        finish(false, result.error ?? 'Email link is invalid or has expired')
        return
      }
      if (result.ok) {
        finish(true, null)
        return
      }
      const {
        data: { session },
      } = await client.auth.getSession()
      if (session) {
        finish(true, null)
        return
      }
      finish(false, result.error ?? 'Auth session missing!')
    })()

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [client])

  return { ready, checking, error }
}

function isRecoveryLikeEvent(event: string): boolean {
  return event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION'
}
